/* ───────────────────────────────────────────────────────────────────────────
   RavishaWhatsApp — QR login page for Ravisha's WhatsApp bot.

   URL: /ravisha-wa
   Reads settings/whatsapp_ravisha from Firestore. Shows the latest QR when
   the bot is in qr_needed state, and shows "Connected as +91..." once she
   scans. A "Disconnect & re-scan" button writes logoutRequest:true which the
   bot picks up to clear its session.
   ─────────────────────────────────────────────────────────────────────────── */

import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";

const GOLD = "#F2B100", GOLD_LO = "#8A6400";
const INK = "#1A1A1E", TXT = "#5C5C66", MUTE = "#9A9AA2";
const LINE = "#E6E6E9", BG = "#F6F6F7", CARD = "#FFFFFF";
const GREEN = "#16A34A";

export default function RavishaWhatsApp() {
  const [state, setState] = useState(null);   // settings/whatsapp_ravisha
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "whatsapp_ravisha"),
      snap => setState(snap.exists() ? snap.data() : { status: "loading" }),
      () => setState({ status: "error" }));
    return () => unsub();
  }, []);

  async function disconnect() {
    if (!window.confirm("Disconnect Ravisha's WhatsApp and generate a fresh QR?")) return;
    setBusy(true);
    try {
      await setDoc(doc(db, "settings", "whatsapp_ravisha"),
        { logoutRequest: true, updatedAt: serverTimestamp() }, { merge: true });
    } finally { setBusy(false); }
  }

  const status = (state && state.status) || "loading";
  const connected = status === "connected";
  const qr = state && state.qrCode;
  const num = state && state.connectedNumber;
  const lastBeat = state && state.lastHeartbeat
    ? state.lastHeartbeat.toDate ? state.lastHeartbeat.toDate() : new Date(state.lastHeartbeat)
    : null;
  const beatAge = lastBeat ? Math.round((Date.now() - lastBeat.getTime()) / 1000) : null;

  return (
    <div style={{ minHeight: "100vh", background: BG,
      fontFamily: "'Archivo',-apple-system,sans-serif", padding: "48px 24px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Space+Mono:wght@700&display=swap');`}</style>

      <div style={{ maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, fontWeight: 700,
          letterSpacing: ".18em", color: GOLD_LO, textTransform: "uppercase" }}>
          NM Squad · Ravisha
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 900, color: INK, margin: "10px 0 6px",
          letterSpacing: "-.02em" }}>
          {connected ? "WhatsApp connected" : "Scan to connect your WhatsApp"}
        </h1>
        <p style={{ fontSize: 14.5, color: TXT, lineHeight: 1.55, margin: "0 0 28px" }}>
          {connected
            ? "You're live. The bot is now handling TUAP groups and bulk messages on your behalf."
            : "Open WhatsApp on your phone → Settings → Linked devices → Link a device → scan the QR code below."}
        </p>

        <div style={{ background: CARD, border: `2px solid ${LINE}`, borderRadius: 18,
          padding: 24, boxShadow: "0 14px 40px rgba(20,20,30,.06)" }}>
          {connected ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <div style={{ width: 92, height: 92, borderRadius: 999, background: GREEN,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 0 8px ${GREEN}22, 0 12px 28px ${GREEN}44` }}>
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="4"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: MUTE, letterSpacing: ".1em" }}>
                CONNECTED AS
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: INK,
                fontFamily: "'Space Mono',monospace" }}>+{num || "—"}</div>
              {beatAge != null && (
                <div style={{ fontSize: 12, fontWeight: 700,
                  color: beatAge < 120 ? GREEN : "#D9730D" }}>
                  Heartbeat {beatAge}s ago
                </div>
              )}
            </div>
          ) : status === "qr_needed" && qr ? (
            <div>
              <img src={qr} alt="Scan this QR with WhatsApp"
                style={{ width: 280, height: 280, display: "block", margin: "0 auto",
                  borderRadius: 8 }} />
              <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: MUTE }}>
                The QR refreshes itself every minute or so.
              </div>
            </div>
          ) : status === "loading" || !state ? (
            <div style={{ padding: "60px 0" }}>
              <div style={{ width: 36, height: 36, borderRadius: 999, border: `3px solid ${LINE}`,
                borderTopColor: GOLD, margin: "0 auto",
                animation: "rwspin 1s linear infinite" }} />
              <style>{`@keyframes rwspin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ marginTop: 14, color: MUTE, fontSize: 13, fontWeight: 700 }}>
                Waking the bot up…
              </div>
            </div>
          ) : status === "disconnected" ? (
            <div style={{ padding: "30px 0" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#D9730D", marginBottom: 8 }}>
                Bot is disconnected
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: TXT, lineHeight: 1.55 }}>
                Sit tight — the QR will appear here as soon as the bot restarts (usually within 30
                seconds). Refreshing this page won't help; the bot is doing it for you.
              </div>
            </div>
          ) : (
            <div style={{ padding: "30px 0", fontSize: 13.5, fontWeight: 700, color: TXT }}>
              Status: {status}
            </div>
          )}
        </div>

        {connected && (
          <button onClick={disconnect} disabled={busy}
            style={{ marginTop: 22, padding: "11px 22px", background: "none",
              border: `1.5px solid ${LINE}`, borderRadius: 999, cursor: "pointer",
              fontFamily: "'Space Mono',monospace", fontSize: 11.5, fontWeight: 700,
              letterSpacing: ".08em", color: TXT, textTransform: "uppercase",
              opacity: busy ? 0.6 : 1 }}>
            {busy ? "Disconnecting…" : "Disconnect & re-scan"}
          </button>
        )}

        <div style={{ marginTop: 36, fontSize: 11.5, fontWeight: 700, color: MUTE,
          fontFamily: "'Space Mono',monospace" }}>
          PORT 3004 · pm2: nm-squad-bot-ravisha · 24/7 watchdog active
        </div>
      </div>
    </div>
  );
}
